import { useMemo, useState } from 'react';
import { apiClient, useAuth } from '../../auth-lib';
import { Button, Input, Select, Modal, useToast } from '../../design-system';
import HoursMinutesInput, {
  formatMinutes,
  splitMinutes,
  joinMinutes,
  totalLogged,
} from './HoursMinutesInput';

const MANAGER_LEVEL = 3;

// Time allocated vs time actually spent, broken down per employee.
// `canEditAllocation` gates the budget editor (a content edit — owner/management
// only, mirroring the server). Logging your own time is always available, so a
// plain assignee still gets a clean "Log time spent" without a Save that 403s.
function TodoTimeModal({ todo, orgUsers = [], onClose, onUpdated, canEditAllocation = true }) {
  const { user } = useAuth();
  const toast = useToast();
  const isManager = (user?.role?.level || 0) >= MANAGER_LEVEL;

  const initial = splitMinutes(todo.allocatedMinutes);
  const [allocH, setAllocH] = useState(initial.hours);
  const [allocM, setAllocM] = useState(initial.minutes);
  const [savingAlloc, setSavingAlloc] = useState(false);

  const [logH, setLogH] = useState('');
  const [logM, setLogM] = useState('');
  const [logNote, setLogNote] = useState('');
  const [logFor, setLogFor] = useState(''); // '' = myself
  const [logging, setLogging] = useState(false);
  const [error, setError] = useState('');

  const logged = totalLogged(todo);
  const budget = todo.allocatedMinutes;
  const overBudget = budget != null && logged > budget;

  // A new entry can't exceed the time the to-do has actually existed (mirrors
  // the server rule). Existing entries are never re-checked.
  const elapsedSinceCreated = Math.max(
    0,
    Math.ceil((Date.now() - new Date(todo.createdAt).getTime()) / 60000)
  );

  // The "who spent what" mapping — group the raw logs by employee.
  const perEmployee = useMemo(() => {
    const byUser = new Map();
    (todo.timeLogs || []).forEach((l) => {
      const id = l.employeeId?._id || l.employeeId || 'unknown';
      const name = l.employeeId?.name || 'Unknown';
      if (!byUser.has(id)) byUser.set(id, { name, minutes: 0, entries: 0 });
      const row = byUser.get(id);
      row.minutes += l.minutes || 0;
      row.entries += 1;
    });
    return Array.from(byUser.values()).sort((a, b) => b.minutes - a.minutes);
  }, [todo.timeLogs]);

  const saveAllocation = async () => {
    setSavingAlloc(true);
    setError('');
    try {
      // Both boxes empty => null => "no budget".
      const value = joinMinutes(allocH, allocM);
      const res = await apiClient.put(`/api/todos/${todo._id}`, { allocatedMinutes: value });
      onUpdated(res.data);
      toast.success('Allocated time saved');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save allocated time');
    } finally {
      setSavingAlloc(false);
    }
  };

  const addLog = async () => {
    const mins = joinMinutes(logH, logM);
    if (!mins || mins <= 0) {
      setError('Enter the time spent');
      return;
    }
    // Recompute fresh so we don't reject over a few stale render-time seconds.
    const cap = Math.max(0, Math.ceil((Date.now() - new Date(todo.createdAt).getTime()) / 60000));
    if (mins > cap) {
      setError(`You can only log up to ${formatMinutes(cap)} — the time since this to-do was created.`);
      return;
    }
    setLogging(true);
    setError('');
    try {
      const res = await apiClient.post(`/api/todos/${todo._id}/timelogs`, {
        minutes: mins,
        note: logNote.trim() || undefined,
        employeeId: logFor || undefined,
      });
      onUpdated(res.data);
      setLogH('');
      setLogM('');
      setLogNote('');
      setLogFor('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to log time');
    } finally {
      setLogging(false);
    }
  };

  const removeLog = async (logId) => {
    try {
      const res = await apiClient.delete(`/api/todos/${todo._id}/timelogs/${logId}`);
      onUpdated(res.data);
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Failed to remove time log');
    }
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={`Time · ${todo.title}`}
      footer={<Button variant="secondary" onClick={onClose}>Close</Button>}
    >
      <div className="todo-time">
        {error && <div className="form-error" role="alert">{error}</div>}

        {/* Allocated vs actual, at a glance. */}
        <div className="todo-time-summary">
          <div className="todo-time-stat">
            <span className="todo-time-label">Allocated</span>
            <strong>{formatMinutes(budget)}</strong>
          </div>
          <div className="todo-time-stat">
            <span className="todo-time-label">Spent</span>
            <strong className={overBudget ? 'todo-time-over' : ''}>{formatMinutes(logged)}</strong>
          </div>
          <div className="todo-time-stat">
            <span className="todo-time-label">{overBudget ? 'Over by' : 'Remaining'}</span>
            <strong className={overBudget ? 'todo-time-over' : ''}>
              {budget == null ? '—' : formatMinutes(Math.abs(budget - logged))}
            </strong>
          </div>
        </div>

        {/* Editing the budget is a content change — owner/management only. A
            plain assignee sees the allocated value in the summary above and goes
            straight to logging their time. */}
        {canEditAllocation && (
          <>
            <div className="todo-time-row">
              <HoursMinutesInput
                label="Allocated time"
                hours={allocH}
                minutes={allocM}
                onChange={(h, m) => { setAllocH(h); setAllocM(m); }}
                helperText="Leave both blank for no budget"
              />
              <Button variant="secondary" onClick={saveAllocation} loading={savingAlloc}>Save</Button>
            </div>

            <hr className="todo-time-sep" />
          </>
        )}

        {/* Log actual time. */}
        <div className="todo-time-row">
          <HoursMinutesInput
            label="Log time spent"
            hours={logH}
            minutes={logM}
            onChange={(h, m) => { setLogH(h); setLogM(m); }}
            helperText={`Up to ${formatMinutes(elapsedSinceCreated)} — the time since this to-do was created`}
          />
          <Input
            label="Note"
            placeholder="What did you do?"
            value={logNote}
            onChange={(e) => setLogNote(e.target.value)}
          />
          {/* Only a manager may log on someone else's behalf (enforced server-side too). */}
          {isManager && (
            <Select label="For" value={logFor} onChange={(e) => setLogFor(e.target.value)}>
              <option value="">Myself</option>
              {orgUsers.map((u) => <option key={u._id} value={u._id}>{u.name}</option>)}
            </Select>
          )}
          <Button variant="primary" onClick={addLog} loading={logging}>Log</Button>
        </div>

        {/* Per-employee mapping. */}
        {perEmployee.length > 0 && (
          <>
            <div className="todo-time-title">Time by employee</div>
            <div className="todo-time-people">
              {perEmployee.map((p) => (
                <div key={p.name} className="todo-time-person">
                  <span>{p.name}</span>
                  <span className="todo-time-person-meta">
                    {p.entries} {p.entries === 1 ? 'entry' : 'entries'}
                  </span>
                  <strong>{formatMinutes(p.minutes)}</strong>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Raw log entries. */}
        {(todo.timeLogs || []).length > 0 && (
          <>
            <div className="todo-time-title">Entries</div>
            <div className="todo-time-logs">
              {[...todo.timeLogs]
                .sort((a, b) => new Date(b.loggedAt) - new Date(a.loggedAt))
                .map((l) => (
                  <div key={l._id} className="todo-time-log">
                    <span className="todo-time-log-who">{l.employeeId?.name || 'Unknown'}</span>
                    <span className="todo-time-log-hrs">{formatMinutes(l.minutes)}</span>
                    <span className="todo-time-log-note">{l.note || '—'}</span>
                    <span className="todo-time-log-date">
                      {l.loggedAt ? new Date(l.loggedAt).toLocaleDateString('en-IN') : ''}
                    </span>
                    <button type="button" className="btn-remove-icon" onClick={() => removeLog(l._id)}>×</button>
                  </div>
                ))}
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

export default TodoTimeModal;
